import className from "classnames";
import { Immutable } from "immer";
import React from "react";

import { STR_NO, STR_YES } from "../../../constants";
import {
    IOptionCode,
    IOptionSingleValue,
    IOptionValues,
    IProduct,
} from "../../../models/catalogue";
import { isoImageURL } from "../../../models/nominals";
import { useAppDispatch, useAppSelector } from "../../../store/reducers";
import { PLCInfoPanel } from "../PLCInfoPanel";
import { defaults } from "../defaults";
import {
    IPLCBooleanOptionSelectorOption,
    IPLCOptionPanel,
    IPLCProductCategorySelector,
} from "../models";
import { getMostSimilarProduct } from "../product-similarity";
import { setOptionValue } from "../reducers";
import { ISelectedCategories } from "../reducers.interfaces";
import { selectedCategoriesSelector } from "../selectors";
import { getOptionValueByIdx } from "../utils";

// Load styles
import "./index.scss";

const BEM_NAME = "plc-boolean-option-selector";

/**
 * Check if selecting this option will result in a perfect product match or not. If
 * not, the option should be visually disabled (but still actually selectable).
 */
function checkIsDisabled(
    option: IOptionSingleValue,
    categorySelectors: readonly IPLCProductCategorySelector[],
    optionPanels: readonly IPLCOptionPanel[],
    rootProducts: readonly IProduct[],
    selectedCategories: ISelectedCategories,
    optionValues: IOptionValues,
    selectorAttribute: IOptionCode,
): boolean {
    const futureOptionValues = {
        ...optionValues,
        [selectorAttribute]: option,
    };
    const result = getMostSimilarProduct({
        categorySelectors,
        optionPanels,
        rootProducts,
        selectedCategories,
        optionValues: futureOptionValues,
        previouslySelectedCategories: selectedCategories,
        previouslySelectedOptionValues: optionValues,
    });
    return !result || result.distance > 0;
}

function generateOptionID(groupID: string, selectorName: string): string {
    return `${groupID}-${selectorName}`
        .replace(/[^a-zA-Z0-9-]/gi, "")
        .toLowerCase();
}

function handleOptionChange(
    isChecked: boolean,
    dispatch: ReturnType<typeof useAppDispatch>,
    selectorAttribute: IOptionCode,
): void {
    const value = !isChecked ? STR_YES : STR_NO;
    dispatch(
        setOptionValue({
            namespace: "",
            code: selectorAttribute,
            index: 0,
            totalNumValues: 1,
            value,
        }),
    );
}

function handleCheckboxChange(
    e: React.ChangeEvent<HTMLInputElement>,
    dispatch: ReturnType<typeof useAppDispatch>,
    selectorAttribute: IOptionCode,
): void {
    const value = e.currentTarget.checked ? STR_YES : STR_NO;
    dispatch(
        setOptionValue({
            namespace: "",
            code: selectorAttribute,
            index: 0,
            totalNumValues: 1,
            value,
        }),
    );
}

export const PLCBooleanOptionSelector = (
    props: Immutable<{
        categorySelectors: readonly IPLCProductCategorySelector[];
        optionPanels: readonly IPLCOptionPanel[];
        groupID: string;
        selector: IPLCBooleanOptionSelectorOption;
    }>,
): React.ReactNode => {
    const dispatch = useAppDispatch();

    const rootProducts = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.entities.rootProducts;
    });

    const selectedCategories = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return selectedCategoriesSelector(state);
    });

    const optionValues = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return state.ui.optionValues;
    });

    const value = useAppSelector((rootState) => {
        const state = rootState.configurator || defaults;
        return getOptionValueByIdx(
            state.ui.optionValues[props.selector.attribute],
            0,
        );
    });

    const optionID = generateOptionID(props.groupID, props.selector.name);
    const descriptionID = `${optionID}-description`;
    const isChecked = value === STR_YES;
    const isDisabled = checkIsDisabled(
        STR_YES,
        props.categorySelectors,
        props.optionPanels,
        rootProducts,
        selectedCategories,
        optionValues,
        props.selector.attribute,
    );

    const listElementClass = className({
        [`${BEM_NAME}__list-element`]: true,
        [`${BEM_NAME}__list-element--faux-disabled`]: isDisabled,
    });
    const buttonClass = className({
        [`${BEM_NAME}`]: true,
        [`${BEM_NAME}--selected`]: isChecked,
        [`${BEM_NAME}--faux-disabled`]: isDisabled,
    });
    const labelClass = className({
        [`${BEM_NAME}__label`]: true,
        [`${BEM_NAME}__label--faux-disabled`]: isDisabled,
    });

    return (
        <li className={listElementClass}>
            <div
                className={buttonClass}
                onClick={() => {
                    handleOptionChange(
                        isChecked,
                        dispatch,
                        props.selector.attribute,
                    );
                }}
            >
                <div className={`${BEM_NAME}__icon`}>
                    {props.selector.image && (
                        <img
                            src={isoImageURL.unwrap(props.selector.image.url)}
                            alt={props.selector.name}
                        />
                    )}
                </div>
                <div className={`${BEM_NAME}__copy`}>
                    <div>
                        <label
                            htmlFor={optionID}
                            className={labelClass}
                            aria-selected={false}
                            aria-describedby={descriptionID}
                        >
                            {props.selector.name}
                        </label>
                    </div>
                    <p
                        id={descriptionID}
                        className={`${BEM_NAME}__description`}
                    >
                        {props.selector.description}
                    </p>
                </div>
                <div className={`${BEM_NAME}__input-container`}>
                    <input
                        type="checkbox"
                        className={`${BEM_NAME}__input`}
                        id={optionID}
                        name={props.selector.attribute}
                        onChange={(e) => {
                            handleCheckboxChange(
                                e,
                                dispatch,
                                props.selector.attribute,
                            );
                        }}
                        checked={value === STR_YES}
                    />
                </div>
            </div>
            <PLCInfoPanel
                buttonText={"Learn More"}
                content={props.selector.help_content}
            />
        </li>
    );
};
